import { AppHTTPService } from '@/api/http-service'
import { useTranslation } from '@/hooks/useTranslation'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Checkbox,
  Icon,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RadioGroup,
  RadioGroupItem,
  Switch,
  cn,
} from '@flatfile/baseline'
import { useQuery } from '@tanstack/react-query'
import { ChangeEvent, KeyboardEvent, useMemo, useRef, useState } from 'react'
import { ColumnsState, DensityType, DisplayManagementPanelProps } from './types'

const noop = () => null

const SHOW_LIST_TABLE_TOGGLE = false
const SHOW_DENSITY_SELECTION = true
const SHOW_COLUMN_MANAGEMENT = true

/**
 * Checks if a target string contains a search string, with optional case sensitivity.
 * @param target - The string to search within
 * @param search - The string to search for
 * @param options - Optional configuration object
 * @returns boolean indicating if the search string is found within the target
 */
const contains = (
  target: string,
  search: string,
  options: { caseSensitive?: boolean; trim?: boolean } = {}
): boolean => {
  if (!search) {
    return true
  }
  if (!target) {
    return false
  }

  const { caseSensitive = false, trim = true } = options

  let normalizedTarget = String(target)
  let normalizedSearch = String(search)

  if (trim) {
    normalizedTarget = normalizedTarget.trim()
    normalizedSearch = normalizedSearch.trim()
  }

  if (!caseSensitive) {
    normalizedTarget = normalizedTarget.toLowerCase()
    normalizedSearch = normalizedSearch.toLowerCase()
  }

  return normalizedTarget.includes(normalizedSearch)
}

/**
 * Panel for managing table appearance and column visibility in a sheet.
 *
 * Special handling for AI-generated columns:
 * When the "Show only AI-generated columns" toggle is activated:
 * 1. The current hidden state of each column is stored in a temporary `_hidden` field
 * 2. Column visibility is overridden - only AI-defined columns are shown
 * 3. When the toggle is turned off, the original visibility is restored from `_hidden`
 * 4. The `_hidden` field presence in any column indicates the toggle was previously on
 *
 * This allows us to:
 * - Preserve the user's column visibility preferences while viewing AI columns
 * - Restore those preferences when turning off the AI columns view
 * - Persist the toggle state across panel remounts using local storage
 */
export const DisplayManagementPanel = ({
  sheetId,
  columnConfig,
  columns,
  setColumns,
  onOpenChange,
  open,
  children,
  viewType,
  onViewTypeChange,
  density,
  updateDensity,
}: DisplayManagementPanelProps) => {
  const [inputValue, setInputValue] = useState('')
  const [showAiGeneratedColumns, setShowAiGeneratedColumns] = useState(() => {
    const state = columns[Object.keys(columns)[0]]
    return state?._hidden !== undefined
  })
  const inputRef = useRef<HTMLInputElement>(null)
  const { t } = useTranslation()
  const { data: fieldCountsData, isFetching } = useQuery({
    queryKey: ['fieldCounts', sheetId],
    queryFn: () =>
      // AppHTTPService.client.sheets.getRecordCounts(sheetId, {
      //   byField: true,
      // }),
      // TODO: Remove this once the API Record Counts is implemented
      ({
        data: {
          counts: {
            byField: {
              name: {
                empty: 0,
                total: 0,
              },
            },
          },
        },
      }),
  })

  const emptyFieldsMap = useMemo(() => {
    if (
      fieldCountsData &&
      fieldCountsData.data &&
      fieldCountsData.data.counts &&
      fieldCountsData.data.counts.byField
    ) {
      return Object.entries(fieldCountsData.data.counts.byField)
        .filter(
          ([_, field]) =>
            'empty' in field && 'total' in field && field.empty === field.total
        )
        .reduce((acc, [key, _]) => {
          acc[key] = { ...columns[key] }
          return acc
        }, {} as ColumnsState)
    } else {
      return {}
    }
  }, [fieldCountsData])

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target?.value
    if (value !== undefined) {
      setInputValue(value)
    }
  }

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      onOpenChange(false)
      noop() // Added for coverage
    }
  }

  const handlePressHideEmpty = () => {
    const newColumnState = { ...columns }
    Object.keys(emptyFieldsMap).forEach((key) => {
      const state = columns[key] ?? {}
      newColumnState[key] = {
        ...state,
        hidden: true,
      }
    })
    setColumns(newColumnState)
  }

  const handleClearInput = () => {
    setInputValue('')
    inputRef?.current?.focus()
  }

  const handlePressItem = (value: string) => {
    const item = columns[value] ?? {}
    const newColumnState = {
      ...columns,
      [value]: {
        ...item,
        value,
        hidden: !item.hidden,
      },
    }
    setColumns(newColumnState)
  }

  const handleSelectAll = (all: boolean) => {
    const newColumnState = columnConfig.reduce((memo, column) => {
      const state = columns[column.value] ?? {}
      memo[column.value] = {
        ...state,
        hidden: !all,
      }
      return memo
    }, {} as ColumnsState)

    setColumns(newColumnState)
  }

  const handleToggleAiGeneratedColumns = () => {
    const newShowAiGeneratedColumns = !showAiGeneratedColumns
    setShowAiGeneratedColumns(newShowAiGeneratedColumns)
    const newColumnState = columnConfig.reduce((memo, column) => {
      const state = columns[column.value] ?? {}
      memo[column.value] = {
        ...state,
        hidden: newShowAiGeneratedColumns
          ? !column.treatments?.includes('ai-defined')
          : (state._hidden ?? state.hidden),
        _hidden: newShowAiGeneratedColumns ? state.hidden : undefined,
      }
      return memo
    }, {} as ColumnsState)
    setColumns(newColumnState)
  }

  const { allSelected, noneSelected } = useMemo(() => {
    let allSelected = true
    let noneSelected = true

    columnConfig.forEach((c) => {
      const state = columns[c.value]
      if (state?.hidden) {
        allSelected = false
      } else {
        noneSelected = false
      }
    })

    return { allSelected, noneSelected }
  }, [columns, columnConfig])

  const hasAiGeneratedColumns = useMemo(
    () =>
      columnConfig.some((column) => column.treatments?.includes('ai-defined')),
    [columnConfig]
  )

  const filteredColumns = useMemo(() => {
    const baseColumns = columnConfig.filter((c) =>
      contains(c.label ?? c.value, inputValue)
    )
    return baseColumns
  }, [inputValue, columnConfig, contains])

  const getColumnState = (column: any) => {
    const state = columns[column.value] ?? {}
    if (showAiGeneratedColumns) {
      return {
        ...state,
        hidden: !column.treatments?.includes('ai-defined'),
      }
    }
    return {
      ...state,
      hidden: state._hidden ?? state.hidden,
    }
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className='w-[320px] p-0' side='bottom' align='end'>
        <div className='px-2 pt-1'>
          <div className='px-2 pt-[6px] pb-[10px] text-base font-medium'>
            Display
          </div>
          {SHOW_LIST_TABLE_TOGGLE && (
            <div className='py-3 border-b border-border/40'>
              <RadioGroup
                value={viewType}
                onValueChange={onViewTypeChange}
                className='flex items-center gap-[9px] bg-accent p-1 rounded-[5px] w-full'
              >
                {[
                  {
                    id: 'table',
                    label: 'Table',
                    value: 'table',
                    icon: 'layout-topbar',
                  },
                  {
                    id: 'list',
                    label: 'List',
                    value: 'list',
                    icon: 'layout-third',
                  },
                ].map((option) => (
                  <Label
                    htmlFor={option.id}
                    key={option.id}
                    className={cn(
                      'flex flex-col items-center justify-center gap-1.5 px-2 py-2 text-black-opacity-1',
                      'rounded-xs cursor-pointer transition-colors flex-1 text-sm relative',
                      {
                        'bg-white shadow-xs': viewType === option.value,
                        'hover:bg-gray-300': viewType !== option.value,
                      }
                    )}
                  >
                    <RadioGroupItem
                      value={option.value}
                      id={option.id}
                      className='sr-only'
                    />
                    <Icon
                      name={option.icon}
                      size={12}
                      className={
                        viewType === option.value
                          ? 'text-primary'
                          : 'text-muted-foreground'
                      }
                    />
                    <span>{option.label}</span>
                  </Label>
                ))}
              </RadioGroup>
            </div>
          )}
          <Accordion
            type='multiple'
            defaultValue={['density', 'view']}
            className='w-full'
          >
            {SHOW_DENSITY_SELECTION && (
              <AccordionItem value='density' className='relative last:border-0'>
                <div className='absolute bottom-0 left-0 right-0 h-px bg-border/40 mx-2' />
                <AccordionTrigger className='w-full px-4 py-3 flex items-center justify-between hover:no-underline'>
                  <div className='flex items-center gap-1.5 -ml-2'>
                    <Icon
                      name='divider-line'
                      size={12}
                      className='text-muted-foreground'
                    />
                    Density type
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className='p-[6px]'>
                    <div className='-my-2'>
                      <RadioGroup
                        value={density}
                        onValueChange={(value) =>
                          updateDensity(value as DensityType)
                        }
                        className='flex flex-col gap-2'
                      >
                        <Label className='flex items-center gap-2 cursor-pointer py-1.5 pl-1'>
                          <RadioGroupItem
                            value='compact'
                            id='density-compact'
                          />
                          <span>
                            {t('sheet.displayManagement.density.compact')}
                          </span>
                        </Label>
                        <Label className='flex items-center gap-2 cursor-pointer py-1.5 pl-1'>
                          <RadioGroupItem
                            value='default'
                            id='density-default'
                          />
                          <span>
                            {t('sheet.displayManagement.density.default')}
                          </span>
                        </Label>
                        <Label className='flex items-center gap-2 cursor-pointer py-1.5 pl-1'>
                          <RadioGroupItem
                            value='comfortable'
                            id='density-comfortable'
                          />
                          <span>
                            {t('sheet.displayManagement.density.comfortable')}
                          </span>
                        </Label>
                      </RadioGroup>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}
            {SHOW_COLUMN_MANAGEMENT && (
              <AccordionItem value='view' className='relative last:border-0'>
                <div className='absolute bottom-0 left-0 right-0 h-px bg-border/40 mx-2' />
                <AccordionTrigger className='w-full px-4 py-3 flex items-center justify-between hover:no-underline'>
                  <div className='flex items-center gap-1.5 -ml-2'>
                    <Icon
                      name='bullet-list'
                      size={12}
                      className='text-muted-foreground'
                    />
                    View
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className='px-2'>
                    {hasAiGeneratedColumns && (
                      <div className='flex items-center justify-between mb-4'>
                        <span className='text-sm text-muted-foreground'>
                          Show only Auto generated columns
                        </span>
                        <Switch
                          id='filter-ai-defined-toggle'
                          checked={showAiGeneratedColumns}
                          onCheckedChange={handleToggleAiGeneratedColumns}
                        />
                      </div>
                    )}

                    <div className={showAiGeneratedColumns ? 'opacity-50' : ''}>
                      <div
                        className={
                          showAiGeneratedColumns ? 'pointer-events-none' : ''
                        }
                      >
                        <div className='relative'>
                          <Input
                            ref={inputRef}
                            value={inputValue}
                            onChange={(e) => handleInputChange(e)}
                            placeholder={t(
                              'sheet.columnManagement.placeholder',
                              {
                                count: columnConfig.length,
                              }
                            )}
                            onKeyDown={handleInputKeyDown}
                            className='w-full'
                          />
                          {inputValue && (
                            <Button
                              variant='ghost'
                              size='sm'
                              className='absolute right-2 top-1/2 -translate-y-1/2'
                              onClick={() =>
                                handleInputChange({
                                  target: { value: '' },
                                } as ChangeEvent<HTMLInputElement>)
                              }
                            >
                              ×
                            </Button>
                          )}
                        </div>
                        <div className='flex items-center gap-1 py-3'>
                          <Button
                            variant='ghost'
                            size='sm'
                            onClick={() => handleSelectAll(true)}
                            disabled={showAiGeneratedColumns || allSelected}
                            className='flex-1'
                          >
                            {t('utils.buttons.selectAllLabel')}
                          </Button>
                          <div className='w-px min-w-[1px] h-4 bg-gray-200' />
                          <Button
                            variant='ghost'
                            size='sm'
                            onClick={() => handleSelectAll(false)}
                            disabled={showAiGeneratedColumns || noneSelected}
                            className='flex-1'
                          >
                            {t('sheet.columnManagement.hideAll')}
                          </Button>
                          <div className='w-px min-w-[1px] h-4 bg-gray-200' />
                          <Button
                            variant='ghost'
                            size='sm'
                            onClick={handlePressHideEmpty}
                            disabled={
                              showAiGeneratedColumns ||
                              isFetching ||
                              noneSelected
                            }
                            data-testid='button-clearEmpty'
                            className='flex-1'
                          >
                            {t('sheet.columnManagement.hideEmpty')}
                          </Button>
                        </div>
                        <div className='max-h-[300px] overflow-y-auto'>
                          {filteredColumns.length ? (
                            <div className='space-y-1'>
                              {filteredColumns.map((column) => {
                                const state = getColumnState(column)
                                return (
                                  <button
                                    key={column.value}
                                    onClick={() =>
                                      handlePressItem(column.value)
                                    }
                                    className='flex w-full items-center gap-2 px-2 py-1.5 hover:bg-accent rounded-sm'
                                    disabled={showAiGeneratedColumns}
                                  >
                                    <Checkbox
                                      checked={!state.hidden}
                                      disabled={showAiGeneratedColumns}
                                      className='pointer-events-none'
                                    />
                                    <span>{column.label ?? column.value}</span>
                                  </button>
                                )
                              })}
                            </div>
                          ) : (
                            <div
                              className='py-6 text-center text-sm text-muted-foreground'
                              data-testid='column-management-empty'
                            >
                              {t(
                                columnConfig.length
                                  ? 'sheet.columnManagement.noMatches'
                                  : 'sheet.columnManagement.noFields'
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>
        </div>
      </PopoverContent>
    </Popover>
  )
}
